/**
 * General Rules / How to Play — /(app)/settings/rules
 *
 * Game-agnostic rules reference. Explains the objective, turn flow,
 * dice combat, cards, fortify, regions, and win conditions. Reachable
 * from About. Per-game variant values (starting troops, card schedule
 * etc.) live in the shared in-game/lobby rules modal.
 */

import { COMPACT_HEADER_STYLE } from '../../../lib/gameChrome'
import React, { useRef } from 'react'
import { View, Text, ScrollView, TouchableOpacity, useWindowDimensions, type LayoutChangeEvent } from 'react-native'
import { useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useOrientation } from '../../../hooks/useOrientation'
import { goBackOr } from '../../../lib/goBack'

function Section({
  id,
  title,
  onLayout,
  children,
}: {
  id: string
  title: string
  onLayout: (id: string, e: LayoutChangeEvent) => void
  children: React.ReactNode
}) {
  return (
    <View
      className="bg-paper border-2 border-olive rounded-xl p-4 gap-2"
      onLayout={(e) => onLayout(id, e)}
    >
      <Text className="font-stencil text-ink text-sm uppercase tracking-widest mb-1">
        {title}
      </Text>
      {children}
    </View>
  )
}

function P({ children }: { children: React.ReactNode }) {
  return (
    <Text className="font-typed text-ink text-sm leading-6">{children}</Text>
  )
}

function Item({ children }: { children: React.ReactNode }) {
  return (
    <View className="flex-row gap-2">
      <Text className="font-typed text-ink-soft text-sm leading-6">•</Text>
      <Text className="font-typed text-ink text-sm leading-6 flex-1">{children}</Text>
    </View>
  )
}

interface JumpChip {
  id: string
  label: string
}

const SECTIONS: JumpChip[] = [
  { id: 'objective', label: 'Objective' },
  { id: 'setup', label: 'Setup' },
  { id: 'turn', label: 'Turn' },
  { id: 'dice', label: 'Dice' },
  { id: 'boards', label: 'Boards' },
  { id: 'cards', label: 'Cards' },
  { id: 'elim', label: 'Eliminate' },
  { id: 'regions', label: 'Regions' },
  { id: 'resign', label: 'Resign' },
  { id: 'teams', label: 'Teams' },
  { id: 'winning', label: 'Winning' },
]

function JumpMenu({
  chips,
  onJump,
}: {
  chips: JumpChip[]
  onJump: (id: string) => void
}) {
  return (
    <View className="border-b border-olive bg-canvas">
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: 12, paddingVertical: 8, gap: 8 }}
      >
        {chips.map((c) => (
          <TouchableOpacity
            key={c.id}
            onPress={() => onJump(c.id)}
            accessibilityLabel={`Jump to ${c.label}`}
            accessibilityRole="button"
            className="bg-paper border border-olive rounded-full px-3 py-1.5 items-center justify-center"
            style={{ minHeight: 32 }}
          >
            <Text className="font-stencil text-ink text-[11px] uppercase tracking-widest">
              {c.label}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  )
}

export default function GeneralRulesScreen() {
  const router = useRouter()
  const { width: winWidth } = useWindowDimensions()
  const { isLandscape } = useOrientation()
  const contentWidth = isLandscape ? Math.min(640, winWidth * 0.65) : undefined

  const scrollRef = useRef<ScrollView>(null)
  const sectionYs = useRef<Record<string, number>>({})

  function onSectionLayout(sectionId: string, e: LayoutChangeEvent) {
    sectionYs.current[sectionId] = e.nativeEvent.layout.y
  }

  function jumpTo(sectionId: string) {
    const y = sectionYs.current[sectionId]
    if (y === undefined) return
    scrollRef.current?.scrollTo({ y: Math.max(0, y - 8), animated: true })
  }

  return (
    <SafeAreaView className="flex-1 bg-canvas" edges={['top']}>
      <View className="flex-row items-center px-4 border-b border-olive" style={COMPACT_HEADER_STYLE}>
        <TouchableOpacity
          onPress={() => goBackOr(router, '/(app)/settings/about')}
          accessibilityLabel="Back"
          accessibilityRole="button"
          className="mr-3 p-1"
        >
          <Text className="text-blood text-base font-typed">← Back</Text>
        </TouchableOpacity>
        <Text className="text-ink font-stencil text-xl flex-1 uppercase">How to Play</Text>
      </View>

      <JumpMenu chips={SECTIONS} onJump={jumpTo} />

      <ScrollView
        ref={scrollRef}
        className="flex-1"
        contentContainerStyle={{
          padding: 16,
          gap: 16,
          paddingBottom: 32,
          alignItems: isLandscape ? 'center' : undefined,
        }}
      >
        <View style={isLandscape ? { width: contentWidth, gap: 16 } : { width: '100%', gap: 16 }}>
        <Section id="objective" title="Objective" onLayout={onSectionLayout}>
          <P>
            Conquer the map. The last player with at least one territory
            wins. In a typical game you'll spend several turns building
            up your borders, several more pushing a neighbour off the
            board, and the rest scrambling to keep one of your regions
            intact long enough to mass an offensive.
          </P>
        </Section>

        <Section id="setup" title="Setup" onLayout={onSectionLayout}>
          <P>
            Territories are either claimed one at a time (manual claim)
            or distributed evenly by the server (random). Either way,
            each player starts with a fixed number of starting troops
            and seeds them across the territories they own.
          </P>
          <P>
            Per-game starting troops + selection mode are configurable
            and shown on the game's own Rules screen (game menu → Rules).
          </P>
        </Section>

        <Section id="turn" title="A Turn — Three Phases" onLayout={onSectionLayout}>
          <Item>
            <Text className="font-stencil">Reinforce.</Text> You receive
            new armies and place them on territories you own. The base
            count is max(3, territories ÷ 3). You also receive bonus
            armies for any regions you own outright. Bonus armies from
            card turn-ins and player eliminations are added here too.
          </Item>
          <Item>
            <Text className="font-stencil">Attack.</Text> Roll dice
            against an adjacent enemy territory. Keep attacking as
            long as the attacking territory has more than one army.
            Conquer at least one territory and you earn a card at the
            end of your turn.
          </Item>
          <Item>
            <Text className="font-stencil">Fortify.</Text> Move armies
            from one of your territories to an adjacent friendly one.
            The classic limit is one fortify per turn.
          </Item>
        </Section>

        <Section id="dice" title="Dice Combat" onLayout={onSectionLayout}>
          <P>
            Attacker rolls up to 3 dice (must leave at least 1 army
            behind on the attacking territory). Defender rolls up to 2.
            Each side's dice are sorted high-to-low and compared in
            pairs. The defender wins ties.
          </P>
          <P>
            If you reduce a defender's territory to zero armies, you
            conquer it. At least as many armies as you attacked with
            (≥ the number of attacker dice rolled) move into the
            captured territory automatically; the post-conquest
            transfer window lets you push more in immediately.
          </P>
        </Section>

        <Section id="boards" title="Special Boards" onLayout={onSectionLayout}>
          <P>
            On most boards every die has six sides, every border works
            both ways, and a territory can hold as many armies as you
            like. Some boards change one of those — and when they do,
            the game shows you.
          </P>
          <Item>
            <Text className="font-stencil">Bigger and smaller dice.</Text>{' '}
            A border can give one side dice with more or fewer than six
            sides — a watchtower shelling with nine, a flag defending
            with five. Attack and defence are separate, and so are the
            two ends: a tower can attack with d9 without its neighbours
            rolling d9 back. The dice counts never change (3 attacking,
            2 defending) and the defender still wins ties. Any border
            the board does not mention is a plain d6 both ways, and the
            attack panel shows a chip when an engagement is anything
            else.
          </Item>
          <Item>
            <Text className="font-stencil">One-way borders.</Text> A
            territory can reach a neighbour it cannot be attacked back
            from — a tower firing down into a walled courtyard. Armies
            can also fortify that way, but they cannot come back. The
            territory list marks both directions, "Attacks (1-way)" and
            "Attacked by (1-way)".
          </Item>
          <Item>
            <Text className="font-stencil">Army limits.</Text> A
            territory can cap how many armies it holds, so a strong
            position cannot become an unanswerable doom-stack. A cap
            only limits what you may add; it never removes armies you
            already have.
          </Item>
          <Item>
            <Text className="font-stencil">Fixed openings.</Text> A
            board can deal its own opening instead of the usual claim
            or auto-assign — a supported seat range, a home area each and
            the rest of the map neutral. When it does, the starting
            troops setting does not apply and the board controls the seats.
          </Item>
        </Section>

        <Section id="cards" title="Cards" onLayout={onSectionLayout}>
          <Item>
            You earn one card per turn in which you conquer at least
            one territory. Conquering more than once still only gives
            you one card.
          </Item>
          <Item>
            Cards come in three types: Infantry, Cavalry, and Artillery.
            A set is either three of the same type, or one of each.
          </Item>
          <Item>
            Turn in a set during your reinforce phase to gain bonus
            armies. The bonus schedule (cycle vs. incremental) is
            configurable per game.
          </Item>
          <Item>
            Hand cap is 5. If you hit 5, you MUST turn in a set before
            you can place any other armies that turn.
          </Item>
        </Section>

        <Section id="elim" title="Eliminating a Player" onLayout={onSectionLayout}>
          <P>
            When your attack wipes out the last of another player's
            territories, you eliminate them. You immediately capture
            ALL of their cards and receive a bonus army stack to place
            before resuming your attack. The exact bonus is configurable.
          </P>
          <P>
            If capturing their cards puts you over the 5-card hand cap,
            you must turn in a set before placing.
          </P>
        </Section>

        <Section id="regions" title="Regions" onLayout={onSectionLayout}>
          <P>
            Each map is divided into regions. Hold every territory in
            a region and you get its region bonus armies at the start
            of every reinforce phase, as long as you keep it whole.
            Region bonuses are usually the difference between a
            stalemate and an offensive — but a single contested
            territory inside one of your regions zeros out the bonus
            until you reclaim it.
          </P>
        </Section>

        <Section id="resign" title="Resigning" onLayout={onSectionLayout}>
          <P>
            You can resign at any time from the game menu. Your
            territories become neutral with their existing armies
            still on them — they aren't redistributed. You won't
            take any more turns in that game.
          </P>
        </Section>

        <Section id="teams" title="Team Games" onLayout={onSectionLayout}>
          <P>
            A game can be played in teams of 2, 3 or 4. Turns alternate
            between the teams, and the order of teams is random. Everything
            else is still per player: your reinforcements, region bonuses,
            cards and troop caps are your own.
          </P>
          <Item>
            Place reinforcements on your own territories or a teammate's.
          </Item>
          <Item>
            Fortify from your own territory into one you or a teammate
            hold. You can only attack from your own territories.
          </Item>
          <Item>
            You can attack a teammate, but the attack panel warns you
            first. Knocking a teammate out earns the usual elimination
            rewards.
          </Item>
          <Item>
            Teammates see each other's cards. Only the owner can turn
            them in.
          </Item>
          <Item>
            In fog of war, a team shares one view of the board.
          </Item>
          <P>
            The last team with a player still holding territory wins.
            Every member of that team wins, including teammates who were
            eliminated earlier.
          </P>
        </Section>

        <Section id="winning" title="Winning" onLayout={onSectionLayout}>
          <P>
            Eliminate every other player. The last player still
            holding territory wins. Games that stall (e.g. round cap
            reached with multiple players still standing) end without
            a clear winner.
          </P>
        </Section>

        <View className="items-center pt-2">
          <Text className="font-typed text-ink-soft text-xs italic text-center">
            Specific numbers (starting troops, attack/fortify caps,
            card bonus schedule) are set per game and shown on the
            game's own Rules screen.
          </Text>
        </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  )
}
